import { config } from "../config";

/**
 * Client for the Meeting Angelo API (MRAI backend).
 *
 * Two sign-in paths produce the same session:
 *  - Inside Teams:  teams-js SSO token  -> POST /Auth/teams-sso-login
 *  - On the web:    MSAL Graph token    -> POST /Auth/microsoft-login
 *
 * The session (app JWT + optional Graph token) is kept in sessionStorage so a
 * tab reload inside Teams stays instant without re-running auth.
 */

const SESSION_KEY = "ma-lite-session";

export interface LiteUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  customerId: string;
}

export interface LiteSession {
  jwt: string;
  /** Delegated Graph token (returned by SSO login, or the MSAL token on web). */
  graphToken: string | null;
  user: LiteUser;
  /** Epoch ms when the app JWT expires. */
  expiresAt: number;
}

/** A processed meeting coming from the Meeting Angelo database. */
export interface MeetingReport {
  eventId: string;
  name: string;
  startDateTime: string;
  endDateTime: string;
  participantCount: number;
  isTranscript: boolean;
  processStatus: string | null;
  organizer: string | null;
  joinUrl: string | null;
}

export interface MeetingReportDetail {
  eventId: string;
  name: string;
  startDateTime: string;
  endDateTime: string;
  participantCount: number;
  processStatus: string | null;
  hasSummary: boolean;
  summaryHtml: string;
}

/**
 * Teams meeting join URLs embed the URL-encoded chat/thread id
 * (e.g. "19%3ameeting_xxx%40thread.v2"). Extracts and decodes it so a report
 * can be matched against per-meeting tracking status (chatId-based).
 */
export function extractChatIdFromJoinUrl(joinUrl: string | null | undefined): string | null {
  if (!joinUrl) return null;
  const match = joinUrl.match(/19%3a[^/]+%40thread\.v2/i) ?? joinUrl.match(/19:[^/]+@thread\.v2/i);
  if (!match) return null;
  try {
    return decodeURIComponent(match[0]);
  } catch {
    return match[0];
  }
}

export class ConsentRequiredError extends Error {
  constructor() {
    super("Additional consent is required for Microsoft Graph access.");
    this.name = "ConsentRequiredError";
  }
}

/** Why registering a meeting was refused for lack of a meeting credit (HTTP 402). */
export type CreditLimitReason = "NoActiveSubscription" | "PoolExhausted" | "UserLimitReached";

export class CreditLimitError extends Error {
  constructor(
    public reason: CreditLimitReason,
    public tenantMonthlyCredits: number,
    public tenantRemaining: number,
    public personalRemaining: number | null,
  ) {
    super("No meeting credit available.");
    this.name = "CreditLimitError";
  }
}

// ---------------------------------------------------------------------------
// Session storage
// ---------------------------------------------------------------------------

export function getStoredSession(): LiteSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw) as LiteSession;
    // Treat sessions expiring in <2 minutes as expired.
    if (!session.jwt || Date.now() > session.expiresAt - 2 * 60 * 1000) {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

function storeSession(session: LiteSession): void {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function clearSession(): void {
  sessionStorage.removeItem(SESSION_KEY);
}

// ---------------------------------------------------------------------------
// Sign-in
// ---------------------------------------------------------------------------

interface AuthResponse {
  accessToken: string;
  graphAccessToken?: string;
  accessTokenExpiresAt: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    customerId: string;
  };
}

function toSession(data: AuthResponse, fallbackGraphToken: string | null): LiteSession {
  const session: LiteSession = {
    jwt: data.accessToken,
    graphToken: data.graphAccessToken ?? fallbackGraphToken,
    user: {
      id: data.user?.id ?? "",
      email: data.user?.email ?? "",
      firstName: data.user?.firstName ?? "",
      lastName: data.user?.lastName ?? "",
      customerId: data.user?.customerId ?? "",
    },
    expiresAt: data.accessTokenExpiresAt
      ? new Date(data.accessTokenExpiresAt).getTime()
      : Date.now() + 55 * 60 * 1000,
  };
  storeSession(session);
  return session;
}

/** Signs in with the token from teams-js authentication.getAuthToken(). */
export async function loginWithTeamsSso(ssoToken: string): Promise<LiteSession> {
  const response = await fetch(`${config.apiBaseUrl}/Auth/teams-sso-login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ssoToken }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    if (body?.consentRequired) {
      throw new ConsentRequiredError();
    }
    throw new Error(body?.message ?? `SSO login failed (${response.status})`);
  }
  return toSession((await response.json()) as AuthResponse, null);
}

/** Decodes the payload of a JWT without validating it (client-side hints only). */
function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    const payload = token.split(".")[1];
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/** Signs in with a Microsoft Graph access token acquired via MSAL. */
export async function loginWithMicrosoft(graphToken: string): Promise<LiteSession> {
  const claims = decodeJwtPayload(graphToken);
  const response = await fetch(`${config.apiBaseUrl}/Auth/microsoft-login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      accessToken: graphToken,
      accountInfo: {
        tenantId: (claims.tid as string) ?? "",
        tenantName: "",
        username: (claims.upn as string) ?? (claims.unique_name as string) ?? "",
        name: (claims.name as string) ?? "",
      },
    }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.message ?? `Microsoft login failed (${response.status})`);
  }
  return toSession((await response.json()) as AuthResponse, graphToken);
}

// ---------------------------------------------------------------------------
// Meeting reports
// ---------------------------------------------------------------------------

async function apiGet<T>(path: string, jwt: string): Promise<T> {
  const response = await fetch(`${config.apiBaseUrl}${path}`, {
    headers: { Authorization: `Bearer ${jwt}` },
  });
  if (response.status === 401) {
    clearSession();
    throw new Error("Your session expired. Please sign in again.");
  }
  if (!response.ok) {
    throw new Error(`API request failed (${response.status})`);
  }
  return (await response.json()) as T;
}

async function apiDelete(path: string, jwt: string): Promise<void> {
  const response = await fetch(`${config.apiBaseUrl}${path}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${jwt}` },
  });
  if (response.status === 401) {
    clearSession();
    throw new Error("Your session expired. Please sign in again.");
  }
  if (!response.ok && response.status !== 204) {
    throw new Error(`API request failed (${response.status})`);
  }
}

// ---------------------------------------------------------------------------
// Role / organization admin (mirrors the roles & org preferences from the
// full Meeting Angelo app, scoped down for Teams Lite). Admin/SuperAdmin only.
// ---------------------------------------------------------------------------

/** The signed-in user's role and profile, as known by the backend (source of truth for role). */
export interface MyProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  customerId: string;
  role: number;
  /** Language entity id driving which language THIS user's AI meeting summaries are written in. */
  languageId?: string | null;
}

export const ROLE_SUPER_ADMIN = 1;
export const ROLE_ADMIN = 2;
export const ROLE_BILLING = 7;

export function isAdminRole(role: number | null): boolean {
  return role === ROLE_SUPER_ADMIN || role === ROLE_ADMIN;
}

/** Who can view the tenant's meeting-credit pool and distribute it among users. */
export function canManageCredits(role: number | null): boolean {
  return role === ROLE_SUPER_ADMIN || role === ROLE_ADMIN;
}

export const ROLE_LABELS: Record<number, string> = {
  1: "Super admin",
  2: "Admin",
  3: "User",
  4: "Report viewer",
  5: "Manager",
  6: "Auditor",
  7: "Billing",
};

/** Fetches the signed-in user's role. Role is never stored in the cached session — always read fresh. */
export async function getMe(jwt: string): Promise<MyProfile> {
  return apiGet<MyProfile>("/User/Me", jwt);
}

export interface Language {
  id: string;
  name: string;
  shortName: string;
}

/** The same global language list used by the full Meeting Angelo app for AI summary output language. */
export async function getLanguages(jwt: string): Promise<Language[]> {
  const data = await apiGet<Array<Record<string, unknown>>>("/Language/GetAllLanguages", jwt);
  return data
    .map((l) => ({
      id: String(l.id ?? ""),
      name: String(l.name ?? ""),
      shortName: String(l.shortName ?? ""),
    }))
    .filter((l) => l.id);
}

/** Updates only the signed-in user's AI-summary language preference (their own profile, everyone can call this). */
export async function updateMyLanguage(jwt: string, languageId: string): Promise<void> {
  await apiPost("/User/UpdateMyProfile", jwt, { languageId });
}

export interface OrgUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: number;
}

/**
 * Lists every user belonging to the signed-in admin's organization.
 * customerId must be passed explicitly: for SuperAdmin/ReportViewer callers
 * the backend only scopes to their own customer when a customerId is given,
 * otherwise it silently returns an empty list.
 */
export async function getOrgUsers(jwt: string, customerId: string): Promise<OrgUser[]> {
  const users = await apiGet<Array<Record<string, unknown>>>(
    `/User/GetUsersByCustomerId?customerId=${encodeURIComponent(customerId)}`,
    jwt,
  );
  return users.map((u) => ({
    id: String(u.id ?? ""),
    email: String(u.email ?? ""),
    firstName: String(u.firstName ?? ""),
    lastName: String(u.lastName ?? ""),
    role: Number(u.role ?? 3),
  }));
}

/**
 * Changes a user's role. Re-fetches the full record first so unrelated fields
 * (password hash, language, etc.) are preserved and never round-tripped
 * through this app's UI layer.
 */
export async function updateOrgUserRole(
  jwt: string,
  customerId: string,
  userId: string,
  newRole: number,
): Promise<void> {
  const users = await apiGet<Array<Record<string, unknown>>>(
    `/User/GetUsersByCustomerId?customerId=${encodeURIComponent(customerId)}`,
    jwt,
  );
  const target = users.find((u) => u.id === userId);
  if (!target) throw new Error("User not found");
  await apiPost("/User/UpdateUser", jwt, {
    id: target.id,
    email: target.email,
    firstName: target.firstName,
    lastName: target.lastName,
    customerId: target.customerId,
    role: newRole,
    languageId: target.languageId,
    customRoleId: target.customRoleId ?? null,
    preferredSummaryTemplate: target.preferredSummaryTemplate ?? null,
  });
}

export interface OrgDefaults {
  organizationName: string;
  summaryTemplate: number;
  meetingSendingType: number;
}

/**
 * Reads the two organization-wide preferences that also exist in the full
 * Meeting Angelo app (summary template, who summaries go to by default).
 * The full customer record (which also holds integration secrets) is only
 * ever touched inside this function — it never reaches component state.
 */
export async function getOrgDefaults(jwt: string, customerId: string): Promise<OrgDefaults> {
  const customer = await apiGet<Record<string, unknown>>(
    `/Customer/GetCustomerById?customerId=${encodeURIComponent(customerId)}`,
    jwt,
  );
  return {
    organizationName: String(customer.name ?? ""),
    summaryTemplate: Number(customer.summaryTemplate ?? 0),
    meetingSendingType: Number(customer.meetingSendingType ?? 0),
  };
}

/** Updates only the given org-wide preference fields, preserving everything else server-side. */
export async function updateOrgDefaults(
  jwt: string,
  customerId: string,
  patch: Partial<Pick<OrgDefaults, "summaryTemplate" | "meetingSendingType">>,
): Promise<void> {
  const customer = await apiGet<Record<string, unknown>>(
    `/Customer/GetCustomerById?customerId=${encodeURIComponent(customerId)}`,
    jwt,
  );
  const merged = { ...customer, ...patch };
  await apiPost("/Customer/UpdateCustomer", jwt, merged);
}

export interface PredefinedUser {
  id: string;
  email: string;
  name: string;
  isActive: boolean;
}

/** Org-wide default summary recipients (used when delivery mode is "predefined users only"). */
export async function getPredefinedUsers(jwt: string, customerId: string): Promise<PredefinedUser[]> {
  const users = await apiGet<Array<Record<string, unknown>>>(
    `/PredefinedUsers/customer/${encodeURIComponent(customerId)}`,
    jwt,
  );
  return users.map((u) => ({
    id: String(u.id ?? ""),
    email: String(u.email ?? ""),
    name: String(u.name ?? ""),
    isActive: Boolean(u.isActive ?? true),
  }));
}

export async function addPredefinedUser(
  jwt: string,
  customerId: string,
  email: string,
  name: string,
): Promise<void> {
  await apiPost("/PredefinedUsers", jwt, { customerId, email, name });
}

export async function removePredefinedUser(jwt: string, id: string): Promise<void> {
  await apiDelete(`/PredefinedUsers/${encodeURIComponent(id)}`, jwt);
}

// ---------------------------------------------------------------------------
// Contact requests
// ---------------------------------------------------------------------------

export interface ContactRequest {
  id: string;
  subject: string;
  details: string;
  urgency: number;
  allowContact: boolean;
  createdDate: string;
  isResolved: boolean;
  submitterName: string;
  submitterEmail: string | null;
  submitterCustomerId: string | null;
}

export async function submitContactRequest(jwt: string, subject: string, details: string): Promise<void> {
  await apiPost("/Feedback/AddFeedback", jwt, {
    category: 4,
    urgency: 2,
    subject,
    details,
    allowContact: true,
  });
}

export async function getContactRequests(jwt: string): Promise<ContactRequest[]> {
  const data = await apiGet<{ items: Array<Record<string, unknown>> }>(
    "/Feedback/GetAllFeedback?pageNumber=1&pageSize=100",
    jwt,
  );
  return (data.items ?? [])
    .filter((item) => Number(item.category) === 4)
    .map((item) => ({
      id: String(item.id ?? ""),
      subject: String(item.subject ?? ""),
      details: String(item.details ?? ""),
      urgency: Number(item.urgency ?? 2),
      allowContact: Boolean(item.allowContact),
      createdDate: String(item.createdDate ?? ""),
      isResolved: Boolean(item.isResolved),
      submitterName: String(item.submitterName ?? ""),
      submitterEmail: item.submitterEmail ? String(item.submitterEmail) : null,
      submitterCustomerId: item.submitterCustomerId ? String(item.submitterCustomerId) : null,
    }));
}

// ---------------------------------------------------------------------------
// Marketplace billing (Teams in-app purchase entitlement)
// ---------------------------------------------------------------------------

/** Current entitlement for the "paidapp-meetingangelo" Marketplace SaaS offer. */
export interface MarketplaceEntitlement {
  configured: boolean;
  offerId: string;
  marketplaceListingUrl: string | null;
  hasActiveSubscription: boolean;
  status: string | null;
  planId: string | null;
  quantity: number | null;
  isFreeTrial: boolean;
  termEndDate: string | null;
  /** Total meetings/month this plan grants the whole tenant (shared pool). */
  monthlyMeetingCredits: number;
  tenantCreditsUsed: number;
  tenantCreditsRemaining: number;
  /** 0 = the signed-in user has no personal cap yet (draws straight from the shared pool). */
  myMonthlyLimit: number;
  myCreditsUsed: number;
  myCreditsRemaining: number | null;
  creditPeriodKey: string;
}

/** Whether the signed-in customer currently has an active paid subscription. */
export async function getBillingEntitlement(jwt: string): Promise<MarketplaceEntitlement> {
  return apiGet<MarketplaceEntitlement>("/TeamsLite/Billing/entitlement", jwt);
}

interface ApiEvent {
  eventId: string;
  name: string;
  startDateTime: string;
  endDateTime: string;
  participants?: { email?: string; type?: string }[] | null;
  isTranscript: boolean;
  processStatus: string | null;
  onlineMeetingJoinUrl?: string | null;
}

/** Latest processed meetings that the signed-in user attended (newest first). */
export async function getMeetingReports(jwt: string, pageSize = 15): Promise<MeetingReport[]> {
  const data = await apiGet<{ items: ApiEvent[]; totalCount: number }>(
    `/Event/GetMyParticipatedReports?pageNumber=1&pageSize=${pageSize}&isTranscript=true`,
    jwt,
  );
  return (data.items ?? []).map((e) => ({
    eventId: e.eventId,
    name: e.name,
    startDateTime: e.startDateTime,
    endDateTime: e.endDateTime,
    participantCount: e.participants?.length ?? 0,
    isTranscript: e.isTranscript,
    processStatus: e.processStatus,
    organizer:
      e.participants?.find((p) => p.type?.toLowerCase() === "organizer")?.email ?? null,
    joinUrl: e.onlineMeetingJoinUrl ?? null,
  }));
}

interface ApiSummary {
  status: boolean;
  eventId: string;
  name: string;
  startDateTime: string;
  endDateTime: string;
  participantCount: number;
  processStatus: string | null;
  hasSummary: boolean;
  summaryHtml: string;
}

/** Full AI summary (HTML) of a single meeting. */
export async function getMeetingReportDetail(
  jwt: string,
  eventId: string,
): Promise<MeetingReportDetail> {
  const data = await apiGet<ApiSummary>(
    `/Event/GetEventSummary?eventId=${encodeURIComponent(eventId)}`,
    jwt,
  );
  return {
    eventId: data.eventId,
    name: data.name,
    startDateTime: data.startDateTime,
    endDateTime: data.endDateTime,
    participantCount: data.participantCount,
    processStatus: data.processStatus,
    hasSummary: data.hasSummary,
    summaryHtml: data.summaryHtml ?? "",
  };
}

// ---------------------------------------------------------------------------
// RSC meeting tracking (Read.ai model — per-meeting consent, no tenant policy)
// ---------------------------------------------------------------------------

export interface TrackedMeetingStatus {
  registered: boolean;
  /** Registered | Subscribed | TranscriptReceived | Processed | Failed */
  status?: string;
  subject?: string | null;
  eventId?: string | null;
  subscriptionActive?: boolean;
  sendToAll?: boolean;
  lastError?: string | null;
}

async function apiPost<T>(path: string, jwt: string, body: unknown): Promise<T> {
  const response = await fetch(`${config.apiBaseUrl}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt}` },
    body: JSON.stringify(body),
  });
  if (response.status === 401) {
    clearSession();
    throw new Error("Your session expired. Please sign in again.");
  }
  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new Error(data?.message ?? `API request failed (${response.status})`);
  }
  return (await response.json()) as T;
}

/**
 * Registers the current meeting for automatic transcript tracking.
 * Backed by RSC permissions granted when the app was added to the meeting —
 * no tenant-wide setup needed. Safe to call repeatedly (idempotent).
 * Throws {@link CreditLimitError} (HTTP 402) when the tenant has no active
 * plan, the shared meeting-credit pool is exhausted, or the signed-in user's
 * personal monthly limit has been reached.
 */
export async function registerMeeting(
  jwt: string,
  chatId: string,
): Promise<TrackedMeetingStatus> {
  const response = await fetch(`${config.apiBaseUrl}/TeamsLite/RegisterMeeting`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt}` },
    body: JSON.stringify({ chatId }),
  });
  if (response.status === 401) {
    clearSession();
    throw new Error("Your session expired. Please sign in again.");
  }
  if (response.status === 402) {
    const body = await response.json().catch(() => null);
    throw new CreditLimitError(
      (body?.reason as CreditLimitReason) ?? "NoActiveSubscription",
      Number(body?.tenantMonthlyCredits ?? 0),
      Number(body?.tenantRemaining ?? 0),
      body?.personalRemaining == null ? null : Number(body.personalRemaining),
    );
  }
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.message ?? body?.Message ?? `API request failed (${response.status})`);
  }
  const data = (await response.json()) as {
    success: boolean;
    status?: string;
    subject?: string | null;
    subscriptionActive?: boolean;
    eventId?: string | null;
  };
  return {
    registered: data.success,
    status: data.status,
    subject: data.subject ?? null,
    eventId: data.eventId ?? null,
    subscriptionActive: data.subscriptionActive ?? false,
  };
}

/** Current tracking status of a meeting the app was added to. */
export async function getTrackedMeetingStatus(
  jwt: string,
  chatId: string,
): Promise<TrackedMeetingStatus> {
  return apiGet<TrackedMeetingStatus>(
    `/TeamsLite/MeetingStatus?chatId=${encodeURIComponent(chatId)}`,
    jwt,
  );
}

/** Asks the backend to check for a transcript right now (fallback to webhook). */
export async function checkMeetingTranscript(
  jwt: string,
  chatId: string,
): Promise<{ success: boolean; transcriptFound: boolean; eventId?: string | null }> {
  return apiPost("/TeamsLite/CheckTranscript", jwt, { chatId });
}

/** Sets whether the summary email goes to everyone in the meeting chat, or just the person who added the app. */
export async function setEmailScope(
  jwt: string,
  chatId: string,
  sendToAll: boolean,
): Promise<{ sendToAll: boolean }> {
  return apiPost("/TeamsLite/SetEmailScope", jwt, { chatId, sendToAll });
}

// ---------------------------------------------------------------------------
// Meeting credits (shared monthly pool granted by the Marketplace plan,
// distributed by an admin into per-user monthly caps).
// ---------------------------------------------------------------------------

/** The signed-in user's own meeting-credit status (everyone can see their own). */
export interface MyCreditStatus {
  hasActiveSubscription: boolean;
  planId: string | null;
  periodKey: string;
  /** 0 = no personal cap yet (shared pool mode). */
  monthlyLimit: number;
  usedThisPeriod: number;
  personalRemaining: number | null;
  tenantMonthlyCredits: number;
  tenantUsedThisPeriod: number;
  tenantRemaining: number;
}

export async function getMyCredits(jwt: string): Promise<MyCreditStatus> {
  return apiGet<MyCreditStatus>("/TeamsLite/Billing/credits/me", jwt);
}

export interface UserCreditInfo {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  role: number;
  monthlyLimit: number;
  usedThisPeriod: number;
  remainingThisPeriod: number | null;
}

/** Tenant-wide credit pool + every user's personal allocation. Admin/Billing/SuperAdmin only. */
export interface TenantCreditSummary {
  hasActiveSubscription: boolean;
  planId: string | null;
  planDisplayName: string | null;
  billingPeriod: string | null;
  monthlyMeetingCredits: number;
  periodKey: string;
  usedThisPeriod: number;
  remainingThisPeriod: number;
  allocatedTotal: number;
  unallocatedTotal: number;
  allocations: UserCreditInfo[];
}

export async function getTenantCredits(jwt: string): Promise<TenantCreditSummary> {
  return apiGet<TenantCreditSummary>("/TeamsLite/Billing/credits/tenant", jwt);
}

/** Sets (or clears with 0) a user's personal monthly meeting cap. Admin/Billing/SuperAdmin only. */
export async function setUserCreditAllocation(
  jwt: string,
  userId: string,
  monthlyLimit: number,
): Promise<UserCreditInfo> {
  return apiPost<UserCreditInfo>("/TeamsLite/Billing/credits/allocate", jwt, { userId, monthlyLimit });
}
